/**
 * Field paths as people would say them: SalesOrderNumber → "Sales order number", customer/customerId →
 * "Customer id", LINE_ID → "Line id". The document peek and the story's panels show these; the raw path
 * stays one visible toggle away in the document peek (never only on hover).
 */
import { humanize } from '../lib/format.ts';

/** The last segment of a path: "shippingAddress/city" → "city" (an attribute's "@" is kept for humanize to drop). */
export const leafOf = (path: string) => path.split('/').filter(Boolean).pop() ?? path;

/** Sentence case: a word in capitals is lowered ("LINE ID" → "Line id"), the first letter stays up. */
function sentence(s: string): string {
  const t = s.split(' ').map((w) => (w.length > 1 && w === w.toUpperCase() && /[A-Z]/.test(w) ? w.toLowerCase() : w)).join(' ');
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/** One path's label, from its leaf: "Sales order number", "Customer id", "Net amount". */
export const fieldLabel = (path: string) => sentence(humanize(leafOf(path)));

/**
 * The labels of a document's fields: the leaf alone, or with its parent when another path of the document
 * ends in the same leaf ("Shipping address · city" beside "Billing address · city"). A path repeated as it
 * is (the lines of an order) needs no parent.
 */
export function fieldLabels(paths: string[]): string[] {
  const byLeaf = new Map<string, Set<string>>();
  for (const p of paths) {
    const l = leafOf(p);
    byLeaf.set(l, (byLeaf.get(l) ?? new Set<string>()).add(p));
  }
  return paths.map((p) => {
    const segs = p.split('/').filter(Boolean);
    const leaf = segs.pop() ?? p;
    const parent = segs.pop();
    if (!parent || (byLeaf.get(leaf)?.size ?? 0) < 2) return fieldLabel(leaf);
    return `${fieldLabel(parent)} · ${fieldLabel(leaf).toLowerCase()}`;
  });
}

/** Identifiers, numbers, codes and addresses of machines are set in mono; names and words are not. */
export const isIdentifier = (v: string) => !/\s/.test(v) && (/\d/.test(v) || v.includes('@'));
